<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="BlanketVOBList" label="Blanket VOB Search" :active="true" />
    </breadcrumb-list>
    <section>
      <div class="container is-max-hd">
        <div class="box">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <div class="title-with-details">
                  <h1 class="title is-4">Blanket VOB Search</h1>
                  <a @click="showBlanketVOBModal = true" class="feature-details-link-title">
                    <span class="details-text">See details</span>
                    <b-icon icon="magnify-plus" size="is-small" class="bounce-icon"></b-icon>
                  </a>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item filter-select-item">
                <b-field expanded>
                  <b-select
                    v-model="selectedUserID"
                    placeholder="Show for User"
                    :loading="loading"
                    @input="handleSelect()"
                    class="user-select"
                    expanded
                  >
                    <option :value="0">All Users</option>
                    <option v-for="opt in userOptions" :value="opt.id" :key="opt.id">
                      {{ truncateName(opt.first_name, opt.last_name) }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="level-item filter-select-item">
                <b-field expanded>
                  <b-select
                    v-model="selectedProviderID"
                    placeholder="Show for Provider"
                    :loading="loading"
                    @input="handleSelect()"
                    class="provider-select"
                    expanded
                  >
                    <option :value="0">All Providers</option>
                    <option v-for="opt in providerOptions" :value="opt.id" :key="opt.id">
                      {{ truncateText(opt.name, 25) }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="level-item">
                <div class="subscription-wrapper">
                  <b-button
                    type="is-primary"
                    icon-left="plus"
                    :disabled="!canCreateBlanketVOB"
                    @click="canCreateBlanketVOB ? $router.push({ name: 'BlanketVOBCreate' }) : null"
                  >
                    Create Blanket VOB Search
                  </b-button>

                  <div
                    v-if="!loading && user && !subscriptionNoticeClosed && blanketVOBNoticeMessage"
                    class="subscription-notice"
                  >
                    <button class="notice-close-btn" @click="closeSubscriptionNotice" type="button">
                      <b-icon icon="close" size="is-small"></b-icon>
                    </button>
                    <p class="has-text-danger has-text-weight-semibold mb-2">
                      {{ blanketVOBNoticeMessage }}
                    </p>
                    <div class="subscription-actions">
                      <b-button
                        type="is-primary"
                        size="is-small"
                        @click="isPastDue ? handleUpdatePayment() : goToSubscriptionPage()"
                      >
                        {{ isPastDue ? 'Update Payment' : 'Enable Feature' }}
                      </b-button>
                      <span class="mx-1">or</span>
                      <a
                        href="https://meetings.hubspot.com/bspircu/blanketvob"
                        target="_blank"
                        class="has-text-link is-size-7"
                      >
                        Talk to Sales
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div class="table-controls-wrapper">
            <b-field class="bulk-actions">
              <b-button
                icon-left="delete"
                type="is-danger"
                v-if="showDeleteButton() && checkedRows.length && isManager"
                @click="confirmBulkRemove"
              >
                Remove
                <span class="bulk-action-text">selected Blanket VOB Searches</span>
              </b-button>
              <b-button
                class="ml-3"
                icon-left="archive"
                type="is-warning"
                v-if="showArchivedButton() && !isRestricted"
                @click="confirmBulkArchive"
              >
                Archive
                <span class="bulk-action-text">selected Blanket VOB Searches</span>
              </b-button>
              <b-button
                class="ml-3"
                icon-left="archive"
                type="is-success"
                v-if="showUnarchivedButton() && !isRestricted"
                @click="confirmBulkUnarchive"
              >
                Unarchive
                <span class="bulk-action-text">selected Blanket VOB Searches</span>
              </b-button>
            </b-field>
            <div class="search-section">
              <b-field>
                <div class="field-body">
                  <b-input
                    class="is-expanded"
                    :loading="loading"
                    v-model="search"
                    placeholder="Search by First or last name, Member ID, or Payer, Group Name"
                    @input="handleSearch"
                  />
                  <div class="archived-toggle-wrapper">
                    <b-tooltip
                      label="Enable archived to see the archived blanket VOBs"
                      position="is-top"
                      type="is-dark"
                      :active="!archivedToggle"
                    >
                      <div class="archived-btn" :class="{ 'is-active': archivedToggle }">
                        <b-switch
                          @input="handleArchiveToggle($event)"
                          :value="archivedToggle"
                          type="is-success"
                        ></b-switch>
                        <span class="archived-btn-text">Archived</span>
                        <span v-if="archivedCount > 0" class="archived-count">
                          {{ archivedCount }}
                        </span>
                      </div>
                    </b-tooltip>
                  </div>
                  <b-dropdown
                    class="filter-dropdown"
                    position="is-bottom-left"
                    v-model="searchableFields"
                    multiple
                    aria-role="list"
                  >
                    <template #trigger>
                      <b-button icon-right="filter" type="is-primary" class="filter-btn">
                        <span class="filter-btn-text">
                          Search Fields
                          <span class="filter-count">({{ searchableFields.length }})</span>
                        </span>
                      </b-button>
                    </template>

                    <b-dropdown-item
                      v-for="field in allFields"
                      :key="field.value"
                      :value="field.value"
                      aria-role="listitem"
                    >
                      <div @click="toggleCheckbox(field.value)">
                        <b-checkbox
                          v-model="searchableFields"
                          :native-value="field.value"
                          class="checkbox-left"
                        >
                          {{ field.label }}
                        </b-checkbox>
                      </div>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </b-field>
            </div>
          </div>

          <div>
            <BlanketVOBTable
              :options="discoveryOptions"
              :current-page="currentPage"
              :page-size="pageSize"
              :total-items="totalItems"
              :loading="loading"
              :archivedToggle="archivedToggle"
              :archivedCount="archivedCount"
              @page-change="handlePageChange"
              @delete="handleDelete"
              @single-archive="handleSingleArchive"
              @search-changed="handleSearchChange"
              @row-click="handleRowClick"
              @create-search="handleCreateSearch"
              @selection-change="handleSelectionChange"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Blanket VOB Feature Detail Modal -->
    <b-modal v-model="showBlanketVOBModal" :width="640" scroll="keep">
      <BlanketVOBFeatureDetail></BlanketVOBFeatureDetail>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import BlanketVOBTable from '../components/BlanketVOBTable.vue';
import BlanketVOBFeatureDetail from '../components/BlanketVOBFeatureDetail.vue';
import request from '../request';
import { mapGetters } from 'vuex';
import { sanitizeSearchInput } from '../utils/form';

export default {
  name: 'BlanketVOBList',
  components: {
    BlanketVOBTable,
    BlanketVOBFeatureDetail,
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 15,
      totalItems: 0,
      selectedUserID: 0,
      selectedProviderID: 0,
      discoveryOptions: [],
      userOptions: [],
      providerOptions: [],
      loading: true,
      search: '',
      searchableFields: [
        'patient_first_name',
        'patient_last_name',
        'member_id',
        'payer_name',
        'group_name',
      ],
      allFields: [
        { value: 'patient_first_name', label: 'First Name' },
        { value: 'patient_last_name', label: 'Last Name' },
        { value: 'member_id', label: 'Member ID' },
        { value: 'payer_name', label: 'Payer Name' },
        { value: 'group_name', label: 'Group Name' },
      ],
      subscriptionNoticeClosed: false,
      showBlanketVOBModal: false,
      checkedRows: [],
      archivedCount: 0,
      archivedToggle: false,
    };
  },

  beforeDestroy() {
    clearTimeout(this._searchDebounceTimer);
  },
  created() {
    axios
      .all([
        request('/users/compact').then((resp) => {
          resp.data.users.sort((a, b) => a.first_name.localeCompare(b.first_name));
          this.userOptions = resp.data.users;
        }),
        request('/providers/compact').then((resp) => {
          resp.data.providers.sort((a, b) => a.name.localeCompare(b.name));
          this.providerOptions = resp.data.providers;
        }),
        this.fetchDiscoveries(),
      ])
      .finally(() => (this.loading = false));
  },
  methods: {
    handleSelect() {
      this.loading = true;
      this.fetchDiscoveries().finally(() => (this.loading = false));
    },
    handleSearch() {
      this.loading = true;
      this.currentPage = 1;
      clearTimeout(this._searchDebounceTimer);
      this._searchDebounceTimer = setTimeout(async () => {
        try {
          await Promise.all([this.fetchDiscoveries(false), this.fetchArchivedCount(false)]);
        } catch (error) {
          this.$buefy.toast.open({
            message: 'An error occurred. Please try again later.',
            type: 'is-danger',
            duration: 5000,
          });
        } finally {
          this.loading = false;
        }
      }, 600);
    },
    toggleCheckbox(value) {
      const index = this.searchableFields.indexOf(value);
      if (index === -1) {
        this.searchableFields.push(value); // Add the field to the selected list
      } else {
        this.searchableFields.splice(index, 1); // Remove the field from the selected list
      }
      this.handleSearch();
    },
    fetchDiscoveries(manageLoading = true) {
      if (manageLoading) this.loading = true;
      const args = {
        page: this.currentPage,
        limit: this.pageSize,
      };

      if (this.selectedUserID !== 0) {
        args.user_id = this.selectedUserID;
      }
      if (this.selectedProviderID !== 0) {
        args.provider_id = this.selectedProviderID;
      }

      // Add search parameters if search term exists
      if (this.search && this.search.trim()) {
        const sanitizedSearch = sanitizeSearchInput(this.search);
        this.searchableFields.forEach((field) => {
          args[field] = sanitizedSearch;
        });
      }
      if (!this.archivedToggle) {
        args['is_archived'] = false;
      }
      const params = new URLSearchParams(args).toString();
      return request(`/blanket-vob-search?${params}`)
        .then((resp) => {
          this.discoveryOptions = resp.data.list;
          // Get total count from header, fallback to list length if not available
          const totalCount = resp.headers['x-total-count'];
          if (totalCount) {
            this.totalItems = parseInt(totalCount, 10);
          } else {
            this.totalItems = resp.data.list.length;
          }
          if (manageLoading) this.loading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$buefy.toast.open({
              message: 'Session expired. Please log in again.',
              type: 'is-danger',
              duration: 5000,
            });
            this.$router.push({ name: 'login' });
          } else {
            this.$buefy.toast.open({
              message: 'An error occurred. Please try again later.',
              type: 'is-danger',
              duration: 5000,
            });
          }
          if (manageLoading) this.loading = false;
        });
    },
    async fetchArchivedCount(manageLoading = true) {
      if (manageLoading) this.loading = true;
      let args = {};
      if (this.selectedUserID !== 0) {
        args.user_id = this.selectedUserID;
      }
      if (this.selectedProviderID !== 0) {
        args.provider_id = this.selectedProviderID;
      }

      if (this.search && this.search.trim()) {
        const sanitizedSearch = sanitizeSearchInput(this.search);
        this.searchableFields.forEach((field) => {
          args[field] = sanitizedSearch;
        });
      }
      if (!this.search) {
        this.archivedCount = 0;
        if (manageLoading) this.loading = false;
        return;
      }
      args['is_archived'] = true;

      let params = new URLSearchParams(args).toString();
      return request(`/blanket-vob-search/count?${params}`)
        .then((resp) => {
          this.archivedCount = resp.data.count;
          if (manageLoading) this.loading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$buefy.toast.open({
              message: 'Session expired. Please log in again.',
              type: 'is-danger',
              duration: 5000,
            });
            this.$router.push({ name: 'login' });
          } else {
            this.$buefy.toast.open({
              message: 'An error occurred. Please try again later.',
              type: 'is-danger',
              duration: 5000,
            });
          }
        });
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchDiscoveries();
    },
    async handleSearchChange({ search, fields }) {
      this.loading = true;
      this.search = sanitizeSearchInput(search);
      this.searchableFields = fields;
      try {
        await Promise.all([this.fetchDiscoveries(false), this.fetchArchivedCount(false)]);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'An error occurred. Please try again later.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
    handleDelete(id) {
      this.loading = true;
      request(`/blanket-vob-search/${id}`, {
        method: 'DELETE',
      })
        .then(() => {
          this.$buefy.toast.open({
            message: 'Blanket VOB Search deleted successfully',
            type: 'is-success',
            duration: 5000,
          });
          this.fetchDiscoveries();
        })
        .catch((error) => {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to delete Blanket VOB Search',
            type: 'is-danger',
            duration: 5000,
          });
          this.loading = false;
        });
    },
    handleSingleArchive(row) {
      const isArchived = row.is_archived;
      const action = isArchived ? 'Unarchive' : 'Archive';

      this.$buefy.dialog.confirm({
        title: `${action} Blanket VOB`,
        message: `Are you sure you want to ${action.toLowerCase()} this Blanket VOB Search?`,
        confirmText: action,
        type: isArchived ? 'is-success' : 'is-warning',
        onConfirm: () => {
          this.loading = true;
          request('/blanket-vob-search/archive', {
            method: 'PUT',
            data: { blanket_vob_ids: [row.id], is_archived: !isArchived },
          })
            .then(() => {
              this.$buefy.toast.open({
                message: `Successfully ${action.toLowerCase()}d Blanket VOB Search`,
                type: 'is-success',
                duration: 5000,
              });
              this.fetchDiscoveries();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message:
                  error.response?.data?.error ||
                  `Failed to ${action.toLowerCase()} Blanket VOB Search`,
                type: 'is-danger',
                duration: 5000,
              });
              this.loading = false;
            });
        },
      });
    },
    handleRowClick(discovery) {
      if (discovery.blanket_vob_payers && discovery.blanket_vob_payers.length > 0) {
        // Route to the first payer response by default
        this.$router.push({
          name: 'BlanketVOBDetails',
          params: {
            id: discovery.id,
            payerResponseId: discovery.blanket_vob_payers[0].id,
          },
        });
      } else {
        // If no payer responses, just route to the discovery details
        this.$router.push({
          name: 'BlanketVOBDetails',
          params: {
            id: discovery.id,
          },
        });
      }
    },
    confirmBulkRemove() {
      let message = `Are you sure you want to remove ${this.checkedRows.length} Blanket VOB Searches?`;
      message += `<br/><br/>NOTE: This will not delete the related data, but these searches will no longer be visible to you or your organization's users.`;

      this.$buefy.dialog.confirm({
        title: 'Remove Blanket VOB Searches',
        message: message,
        confirmText: `Remove ${this.checkedRows.length} searches`,
        type: 'is-danger',
        onConfirm: () => {
          this.handleBulkRemove(this.checkedRows.map((r) => r.id));
        },
      });
    },
    confirmBulkArchive() {
      let message = `Are you sure you want to archive ${this.checkedRows.length} Blanket VOB Searches?`;
      message += `<br/><br/>NOTE: these Blanket VOBs will no longer be visible here. You can find them later in the Archived section.`;

      this.$buefy.dialog.confirm({
        title: 'Archive Blanket VOB Searches',
        message: message,
        confirmText: `Archive ${this.checkedRows.length} searches`,
        type: 'is-danger',
        onConfirm: () => {
          this.handleBulkArchiveUnarchive(
            this.checkedRows.map((r) => r.id),
            true
          );
        },
      });
    },
    confirmBulkUnarchive() {
      let message = `Are you sure you want to Unarchive ${this.checkedRows.length} Blanket VOB Searches?`;
      this.$buefy.dialog.confirm({
        title: 'Unarchive Blanket VOB',
        message: message,
        confirmText: `Unarchive ${this.checkedRows.length} searches`,
        type: 'is-success',
        onConfirm: () => {
          this.handleBulkArchiveUnarchive(
            this.checkedRows.map((r) => r.id),
            false
          );
        },
      });
    },
    handleBulkRemove(ids) {
      this.loading = true;
      request('/blanket-vob-search', {
        method: 'PUT',
        data: { blanket_vob_ids: ids, is_hidden: true },
      })
        .then(() => {
          this.$buefy.toast.open({
            message: `Successfully removed ${ids.length} Blanket VOB Searches`,
            type: 'is-success',
            duration: 10000,
          });
          this.fetchDiscoveries();
        })
        .catch((error) => {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to remove Blanket VOB Searches',
            type: 'is-danger',
            duration: 5000,
          });
          this.loading = false;
        });
    },
    handleBulkArchiveUnarchive(ids, is_archived) {
      this.loading = true;
      request('/blanket-vob-search/archive', {
        method: 'PUT',
        data: { blanket_vob_ids: ids, is_archived: is_archived },
      })
        .then(() => {
          this.$buefy.toast.open({
            message: `Successfully archived ${ids.length} Blanket VOB Searches`,
            type: 'is-success',
            duration: 10000,
          });
          this.fetchDiscoveries();
        })
        .catch((error) => {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to archive Blanket VOB Searches',
            type: 'is-danger',
            duration: 5000,
          });
          this.loading = false;
        });
    },
    goToSubscriptionPage() {
      // Navigate to subscription/billing page with enable feature parameter
      this.$router
        .push({
          path: '/organization/settings/plan',
        })
        .then(() => {
          // Wait for the page to load, then scroll to the Blanket VOB Search section
          this.$nextTick(() => {
            setTimeout(() => {
              // Try to find the Blanket VOB Search section and scroll to it
              const blanketVobElement = document.querySelector(
                '[data-feature="blanket-vob"], [data-toggle="blanket-vob"], .blanket-vob-toggle, #blanket-vob-search'
              );
              if (blanketVobElement) {
                blanketVobElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              } else {
                // If specific element not found, scroll down to show toggles area
                window.scrollTo({
                  top: window.innerHeight,
                  behavior: 'smooth',
                });
              }
            }, 500); // Small delay to ensure page is fully loaded
          });
        })
        .catch(() => {
          // Fallback: use full URL if routing fails
          window.location.href = '/organization/settings/plan?enableFeature=blanket-vob';
        });
    },
    handleUpdatePayment() {
      this.loading = true;
      request.post('/subscriptions/portal-session').then((resp) => {
        window.location.href = resp.data.redirect_url;
      });
    },
    closeSubscriptionNotice() {
      this.subscriptionNoticeClosed = true;
    },
    handleCreateSearch() {
      // Check if user can create blanket VOB search
      if (this.canCreateBlanketVOB) {
        this.$router.push({ name: 'BlanketVOBCreate' });
      } else {
        // Show the subscription notice (make sure it's not closed)
        this.subscriptionNoticeClosed = false;
        // Scroll to the top to show the subscription notice
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    truncateName(firstName, lastName) {
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      if (fullName.length <= 25) return fullName;

      // Try to fit first name + last initial
      if (firstName && lastName) {
        const truncated = `${firstName} ${lastName.charAt(0)}.`;
        if (truncated.length <= 25) return truncated;
      }

      // If still too long, truncate the first name
      if (firstName && lastName) {
        const maxFirstNameLength = 25 - lastName.length - 2; // -2 for " " and "."
        if (maxFirstNameLength > 0) {
          return `${firstName.substring(0, maxFirstNameLength)} ${lastName.charAt(0)}.`;
        }
      }

      // Fallback to simple truncation
      return this.truncateText(fullName, 25);
    },
    handleSelectionChange(rows) {
      this.checkedRows = rows;
    },
    handleArchiveToggle(event) {
      this.archivedToggle = !!event;
      this.fetchDiscoveries();
    },
    showDeleteButton() {
      if (this.checkedRows.length) return true;
      return false;
    },
    showArchivedButton() {
      const hasUnArchived = this.checkedRows.some((row) => row.is_archived === false);
      return hasUnArchived;
    },
    showUnarchivedButton() {
      const hasArchived = this.checkedRows.some((row) => row.is_archived === true);
      return hasArchived;
    },
  },
  computed: {
    ...mapGetters(['isManager', 'isRestricted', 'user']),
    selectedUser() {
      return this.userOptions.find((opt) => opt.id === this.selectedUserID);
    },
    isPastDue() {
      return this.user && this.user.has_past_due_subscription;
    },
    canCreateBlanketVOB() {
      // Don't make any decisions while data is still loading
      if (this.loading || !this.user) {
        return false;
      }

      // For paid plans, allow if has active or past due subscription and the feature
      // (they paid for it!)
      if (
        this.user.has_blanket_vob &&
        (this.user.has_active_subscription || this.user.has_past_due_subscription)
      ) {
        return true;
      }

      // Check if has override access (trial/override access)
      if (this.user.has_override_blanket_vob) {
        // Check if override has expired
        if (this.user.override_blanket_vob_ends_at) {
          const expiryDate = new Date(this.user.override_blanket_vob_ends_at);
          const now = new Date();
          if (now > expiryDate) {
            return false; // Override expired
          }
        }

        // Check if usage limit has been reached
        if (this.user.override_blanket_vob_limit > 0) {
          if (this.user.override_blanket_vob_used >= this.user.override_blanket_vob_limit) {
            return false; // Limit reached
          }
        }

        return true; // Override is valid
      }

      return false;
    },
    blanketVOBNoticeMessage() {
      // Don't show any message while data is still loading
      if (this.loading || !this.user) {
        return null;
      }

      // If user has active paid blanket VOB subscription, don't show any trial messages
      if (this.user.has_blanket_vob && this.user.has_active_subscription) {
        return null;
      }

      // Check if has override access (trial)
      if (this.user.has_override_blanket_vob) {
        // Check if override has expired
        if (this.user.override_blanket_vob_ends_at) {
          const expiryDate = new Date(this.user.override_blanket_vob_ends_at);
          const now = new Date();
          if (now > expiryDate) {
            return 'Your Blanket VOB trial access has expired.';
          }
        }

        // Check if usage limit has been reached
        if (this.user.override_blanket_vob_limit > 0) {
          const remaining =
            this.user.override_blanket_vob_limit - this.user.override_blanket_vob_used;
          if (remaining <= 0) {
            return 'You have reached your Blanket VOB trial limit.';
          }
        }

        // Show simple trial message
        return 'You have trial access to Blanket VOB';
      }

      // If doesn't have blanket VOB feature at all
      if (!this.user.has_blanket_vob) {
        return "You're not subscribed to this feature";
      }

      // If past due, show simple message
      if (this.user.has_past_due_subscription) {
        return 'Subscription past due.';
      }

      return "You're not subscribed to this feature";
    },
  },
};
</script>

<style scoped>
.title {
  margin-bottom: 0 !important;
}

.title-with-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

@media (max-width: 1023px) {
  .title-with-details {
    display: none;
  }
}

.level {
  flex-wrap: wrap;
  gap: 1rem;
}

.level-right {
  flex-wrap: wrap;
  gap: 1rem;
}

.level-item {
  flex-shrink: 0;
}

.feature-details-link-title {
  color: #7c3aed;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
  font-size: 0.875rem;
  text-decoration: none;
}

.feature-details-link-title:hover {
  color: #6d28d9;
  text-decoration: underline;
}

.details-text {
  font-weight: 500;
}

.bounce-icon {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}
.b-field {
  margin-bottom: 0 !important;
}
.search-section {
  margin-bottom: 1rem;
}
.field-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.is-expanded {
  flex-grow: 1;
}
.filter-dropdown {
  margin-left: 10px;
}
/* Prevent the background color on selection */
.filter-dropdown .dropdown-item {
  color: black !important;
}

/*Remove background color on hover or active state */
.filter-dropdown .dropdown-item.is-active,
.filter-dropdown .dropdown-item.is-hovered {
  background-color: transparent !important;
}
.checkbox-left {
  display: inline-block;
  padding-left: 10px;
  margin-right: 10px;
}
.filter-dropdown .dropdown-item .b-checkbox {
  display: inline-flex;
  align-items: center;
}

/* Archived toggle button style */
.archived-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: calc(0.5em - 1px) 1em;
  background-color: #f5f5f5;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 2.5em;
  box-sizing: border-box;
}

.archived-btn:hover {
  background-color: #eeeeee;
  border-color: #b5b5b5;
}

.archived-btn.is-active {
  background-color: #e8e8e8;
  border-color: #b5b5b5;
}

.archived-btn .b-switch {
  margin: 0 !important;
}

.archived-btn-text {
  font-size: 16px;
  font-weight: 400;
  color: #363636;
  white-space: nowrap;
}

.archived-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  background-color: #7a7a7a;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 10px;
  letter-spacing: 0.3px;
}

.archived-toggle-wrapper {
  margin-left: 0.5rem;
}

.subscription-wrapper {
  position: relative;
}

.subscription-notice {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #fef7f7;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  z-index: 10;
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 250px;
}

.subscription-notice p {
  margin-bottom: 0.5rem;
}

.subscription-notice p:last-child {
  margin-bottom: 0;
}

.subscription-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mx-1 {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}

.notice-close-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
}

/* Select box text overflow handling */
.user-select .select select,
.provider-select .select select {
  max-width: 200px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.user-select .select select option,
.provider-select .select select option {
  max-width: 200px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0.5rem;
  line-height: 1.2;
}

/* For dropdown options, allow text to wrap */
.user-select .dropdown-content .dropdown-item,
.provider-select .dropdown-content .dropdown-item {
  white-space: normal;
  word-wrap: break-word;
  max-width: 200px;
  padding: 0.5rem;
  line-height: 1.2;
}
.toggle-label {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1f2937;
}
.tag {
  border-radius: 9999px;
  font-size: 0.85rem;
  padding: 0.25em 0.75em;
}

/* Mobile responsive layout */
@media (max-width: 1023px) {
  .level {
    display: flex;
    flex-direction: column;
    align-items: stretch !important;
  }

  .level-left {
    display: none;
  }

  .level-right {
    display: flex;
    flex-wrap: wrap;
    width: 100% !important;
    gap: 0.75rem;
    margin-left: 0 !important;
    justify-content: stretch;
  }

  /* Create button - show first and full width */
  .level-right .level-item:last-child {
    order: -1;
    width: 100%;
    flex-basis: 100%;
    margin: 0 !important;
  }

  .level-right .level-item:last-child .subscription-wrapper {
    width: 100%;
  }

  .level-right .level-item:last-child .subscription-wrapper > .button {
    width: 100%;
  }

  /* Filter selects - side by side, each taking half width */
  .level-right .filter-select-item {
    flex: 1 1 calc(50% - 0.375rem);
    min-width: 0;
    margin: 0 !important;
    max-width: none;
  }

  .level-right .filter-select-item :deep(.field) {
    width: 100%;
    margin-bottom: 0;
  }

  .level-right .filter-select-item :deep(.control),
  .level-right .filter-select-item :deep(.select) {
    width: 100% !important;
    max-width: none !important;
  }

  .level-right .filter-select-item :deep(.select select) {
    width: 100% !important;
    max-width: none !important;
  }

  /* Search box on top, Archived and Search Fields on same row below */
  .search-section .field-body {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .search-section .field-body .is-expanded {
    flex: 1 1 100%;
    order: 1;
  }

  .search-section .field-body .is-expanded :deep(input) {
    width: 100%;
  }

  /* Archived switch and Search Fields button on same row */
  .search-section .field-body .archived-toggle-wrapper {
    order: 2;
    flex-shrink: 0;
    margin-left: 0;
  }

  .search-section .field-body .archived-btn {
    padding: 0.3em 0.5em;
    gap: 0.25rem;
    height: 2.25em;
  }

  .search-section .field-body .archived-btn-text {
    font-size: 13px;
  }

  .search-section .field-body .archived-count {
    min-width: 1.1rem;
    height: 1.1rem;
    font-size: 0.65rem;
    padding: 0 0.25rem;
  }

  .search-section .field-body .filter-dropdown {
    order: 3;
    flex-shrink: 0;
    margin-left: auto;
  }

  .search-section .field-body .filter-dropdown :deep(.button) {
    padding: 0.4em 0.75em;
    height: 2.35em;
    font-size: 14px;
  }

  /* Hide the count number on mobile */
  .filter-count {
    display: none;
  }

  /* Shorten bulk action button text on mobile */
  .bulk-action-text {
    display: none;
  }

  /* Wrapper for search and bulk actions - reorder on mobile */
  .table-controls-wrapper {
    display: flex;
    flex-direction: column;
  }

  .table-controls-wrapper .search-section {
    order: 1;
  }

  .table-controls-wrapper .bulk-actions {
    order: 2;
    margin-top: 0.5rem;
  }

  /* Make bulk action buttons full width on mobile */
  .bulk-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
  }

  .bulk-actions :deep(.button) {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: calc(50% - 0.25rem);
    margin-left: 0 !important;
  }

  /* When there's only one button, make it full width */
  .bulk-actions :deep(.button:only-child) {
    flex: 1 1 100%;
    min-width: 100%;
  }

  /* Hide mobile sort dropdown and select-all checkbox */
  :deep(.table-mobile-sort) {
    display: none !important;
  }

  :deep(.b-table .table th.checkbox-cell) {
    display: none !important;
  }
}
</style>
